import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Message } from 'primereact/message';
import { stanjeRobeBrandsAPI, stanjeRobeProductsAPI } from '../services/api';
import { StanjeRobeBrand, StanjeRobeProduct } from '../types';
import { showCustomAlert } from './CustomAlert';
import { formatM2, m2PerPallet } from '../utils/stanjeRobeCalc';

const StanjeRobeProducts: React.FC = () => {
  const [products, setProducts] = useState<StanjeRobeProduct[]>([]);
  const [brands, setBrands] = useState<StanjeRobeBrand[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editing, setEditing] = useState<StanjeRobeProduct | null>(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    brandId: '',
    name: '',
    perPallet: null as number | null,
    m2PerPiece: null as number | null,
    description: ''
  });

  const brandOptions = useMemo(() => brands.map((b) => ({ label: b.name, value: b._id })), [brands]);

  const dialogM2PerPallet = useMemo(
    () => m2PerPallet({ m2PerPiece: form.m2PerPiece ?? undefined, perPallet: form.perPallet ?? undefined }),
    [form.m2PerPiece, form.perPallet]
  );

  const load = async () => {
    setLoading(true);
    try {
      const [b, p] = await Promise.all([stanjeRobeBrandsAPI.getAll(), stanjeRobeProductsAPI.getAll()]);
      setBrands(b);
      setProducts(p);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Greška pri učitavanju');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ brandId: brands[0]?._id || '', name: '', perPallet: 24, m2PerPiece: null, description: '' });
    setError('');
    setDialogVisible(true);
  };

  const openEdit = (p: StanjeRobeProduct) => {
    setEditing(p);
    const brandId = typeof p.brandId === 'string' ? p.brandId : p.brandId?._id || '';
    setForm({
      brandId,
      name: p.name,
      perPallet: p.perPallet ?? null,
      m2PerPiece: p.m2PerPiece ?? null,
      description: p.description || ''
    });
    setError('');
    setDialogVisible(true);
  };

  const handleSubmit = async () => {
    if (!form.brandId) { setError('Brend je obavezan'); return; }
    if (!form.name.trim()) { setError('Naziv je obavezan'); return; }
    try {
      const payload = {
        brandId: form.brandId,
        name: form.name.trim(),
        unit: 'kom' as const,
        perPallet: form.perPallet ?? undefined,
        m2PerPiece: form.m2PerPiece ?? undefined,
        description: form.description
      };
      if (editing) await stanjeRobeProductsAPI.update(editing._id, payload);
      else await stanjeRobeProductsAPI.create(payload);
      setDialogVisible(false);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Greška pri čuvanju');
    }
  };

  const handleDelete = (p: StanjeRobeProduct) => {
    showCustomAlert.confirm(`Obrisati robu "${p.name}"?`, async () => {
      try {
        await stanjeRobeProductsAPI.delete(p._id);
        await load();
      } catch (err: any) {
        showCustomAlert.error(err?.response?.data?.message || 'Greška pri brisanju');
      }
    });
  };

  return (
    <div className="p-3 md:p-4">
      <Card title="Roba" subTitle="Svaka roba mora biti povezana sa brendom">
        {error && <Message severity="error" text={error} className="mb-3" />}
        <div className="flex justify-content-end mb-3">
          <Button label="Dodaj robu" icon="pi pi-plus" onClick={openCreate} disabled={!brands.length} />
        </div>
        <DataTable value={products} loading={loading} paginator rows={15} emptyMessage="Nema robe.">
          <Column field="brandName" header="Brend" sortable />
          <Column field="name" header="Naziv" sortable />
          <Column header="Jedinica" body={() => 'kom'} />
          <Column header="m²/kom" body={(r: StanjeRobeProduct) => (r.m2PerPiece ? formatM2(r.m2PerPiece) : '')} />
          <Column field="perPallet" header="kom/paleta" />
          <Column
            header="m²/pal"
            body={(r: StanjeRobeProduct) => {
              const total = m2PerPallet(r);
              return total > 0 ? formatM2(total) : '';
            }}
          />
          <Column
            header="Akcije"
            body={(row: StanjeRobeProduct) => (
              <div className="flex gap-2">
                <Button icon="pi pi-pencil" rounded text onClick={() => openEdit(row)} />
                <Button icon="pi pi-trash" rounded text severity="danger" onClick={() => handleDelete(row)} />
              </div>
            )}
            style={{ width: 120 }}
          />
        </DataTable>
      </Card>

      <Dialog header={editing ? 'Izmeni robu' : 'Nova roba'} visible={dialogVisible} onHide={() => setDialogVisible(false)} style={{ width: '560px' }}>
        <div className="p-fluid formgrid grid">
          <div className="field col-12">
            <label>Brend *</label>
            <Dropdown value={form.brandId} options={brandOptions} onChange={(e) => setForm({ ...form, brandId: e.value })} placeholder="Odaberite brend" className="w-full" />
          </div>
          <div className="field col-12">
            <label>Naziv *</label>
            <InputText value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="field col-12 md:col-4">
            <label>Jedinica</label>
            <InputText value="kom" disabled />
          </div>
          <div className="field col-12 md:col-4">
            <label>m² po komadu</label>
            <InputNumber value={form.m2PerPiece} onValueChange={(e) => setForm({ ...form, m2PerPiece: e.value ?? null })} minFractionDigits={0} maxFractionDigits={4} className="w-full" />
          </div>
          <div className="field col-12 md:col-4">
            <label>Kom po paleti</label>
            <InputNumber value={form.perPallet} onValueChange={(e) => setForm({ ...form, perPallet: e.value ?? null })} className="w-full" />
          </div>
          {dialogM2PerPallet > 0 && (
            <div className="col-12">
              <div className="p-message p-message-info" style={{ margin: 0, padding: '0.5rem 0.75rem' }}>
                <i className="pi pi-info-circle" style={{ marginRight: '0.5rem' }} />
                Jedna paleta = <strong>{formatM2(dialogM2PerPallet)} m²</strong>
                {' '}(kom/paleta × m²/kom)
              </div>
            </div>
          )}
          <div className="field col-12 flex justify-content-end gap-2">
            <Button label="Otkaži" outlined severity="secondary" onClick={() => setDialogVisible(false)} />
            <Button label="Sačuvaj" icon="pi pi-check" onClick={handleSubmit} />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default StanjeRobeProducts;
